import {
  computed,
  DestroyRef,
  inject,
  Service,
  signal,
  Signal,
} from '@angular/core';

import { BreakpointKey, matchAbove } from '@shared/utils';

export type Breakpoint = 'xs' | BreakpointKey;

const ORDER: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
const ABOVE_XS: BreakpointKey[] = ['sm', 'md', 'lg', 'xl', 'xxl'];

const detectCurrent = (): Breakpoint =>
  ABOVE_XS.findLast((breakpoint) => matchAbove(breakpoint).matches) ?? 'xs';

@Service()
export class BreakpointService {
  destroyRef = inject(DestroyRef);

  detectedBreakpoint = signal<Breakpoint>(detectCurrent());

  current: Signal<Breakpoint> = this.detectedBreakpoint.asReadonly();

  constructor() {
    const queries = ABOVE_XS.map((bp) => matchAbove(bp));
    const update = () => this.detectedBreakpoint.set(detectCurrent());

    for (const mql of queries) {
      mql.addEventListener('change', update);
    }

    this.destroyRef.onDestroy(() => {
      for (const mql of queries) {
        mql.removeEventListener('change', update);
      }
    });
  }

  below = (bp: Breakpoint): Signal<boolean> => {
    const idx = ORDER.indexOf(bp);

    return computed(() => ORDER.indexOf(this.detectedBreakpoint()) < idx);
  };

  at = (bp: Breakpoint): Signal<boolean> =>
    computed(() => this.detectedBreakpoint() === bp);

  atOrAbove = (bp: Breakpoint): Signal<boolean> => {
    const idx = ORDER.indexOf(bp);

    return computed(() => ORDER.indexOf(this.detectedBreakpoint()) >= idx);
  };
}
